import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm, NgModel } from '@angular/forms';
import { FilterTypes, NumberOfEmployeesValidCriteria } from '@models/filterEnums.model';
import { StudySearchStrategy } from '@models/searchStrategy.model';
import { FilterService } from '@services/filter.service';
import { LookupService } from '@services/lookup.service';
import { SearchStrategyService } from '@services/search-strategy.service';
import { RegularExpressionPattern } from '@shared/_constants';
import { TreeNode } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterUtilService } from '../shared/filter-util.service';
import _ from 'lodash-es';
import { NumberOfEmployeesCriteria } from './filter-number-of-employees.model';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { selectPeriodOfStudyFrom, selectPeriodOfStudyTo } from '../../study.selectors';

@Component({
  selector: 'app-filter-number-of-employees',
  templateUrl: './filter-number-of-employees.component.html',
  styleUrls: ['./filter-number-of-employees.component.css'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class FilterNumberOfEmployeesComponent implements OnInit {
  @Input() filter: any;
  @Input() readOnly = false;

  numberOfEmployeesAbsoluteYears: TreeNode[];
  numberOfEmployeesValidCriteria: typeof NumberOfEmployeesValidCriteria = NumberOfEmployeesValidCriteria;
  regularExpressionPattern: typeof RegularExpressionPattern = RegularExpressionPattern;

  formDirty = false;
  numberOfEmployeesRelativeYears: TreeNode[];

  searchStrategy: StudySearchStrategy;
  isNonBvdSelected: boolean;
  disablePassIfNa = false;
  periodOfStudyFrom: number;
  periodOfStudyTo: number;

  private destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private searchStrategyService: SearchStrategyService,
    private filterService: FilterService,
    private filterUtilService: FilterUtilService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectPeriodOfStudyFrom))
      .subscribe((from) => {
        this.periodOfStudyFrom = from;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectPeriodOfStudyTo))
      .subscribe((to) => {
        this.periodOfStudyTo = to;
      });

    this.loadInitialData();
    this.setPassIfNaOption();
  }

  initializeForm() {
    if ((<NumberOfEmployeesCriteria>this.filter).absoluteYears) {
      this.filter.absoluteYears = this.flattenTree(this.numberOfEmployeesAbsoluteYears).filter((selection) =>
        this.findNodeRecursive(selection.data, (<NumberOfEmployeesCriteria>this.filter).absoluteYears)
      );
      this.sortSelection(this.filter.absoluteYears);
    }
  }

  ShowValidationMessage(item: NgModel): boolean {
    return (item.errors || false) && (!item.pristine || item.touched);
  }

  loadInitialData() {
    this.filterUtilService.spinnerRS.next(true);
    this.searchStrategy = this.searchStrategyService.studySearchStrategy;

    this.numberOfEmployeesRelativeYears = this.filterService.getNumberOfEmployeeRelativeYears(this.readOnly);
    const selectedDatabaseIds = _.map(this.searchStrategy.studySearchStrategyDatabases, 'databaseId');

    this.lookupService
      .isNonBvdDatabase(selectedDatabaseIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_isNonBvdSelected) => {
        this.isNonBvdSelected = _isNonBvdSelected;
        this.numberOfEmployeesAbsoluteYears = this.filterService.getNumberOfEmployeeAbsoluteYears(
          this.readOnly,
          this.periodOfStudyFrom,
          this.periodOfStudyTo
        );
        this.initializeForm();
        this.filterUtilService.spinnerRS.next(false);
      });
  }

  public numberOfEmployeesTabChange() {
    if (this.filter instanceof NumberOfEmployeesCriteria) {
      this.filter.relativeYears = [];
      this.filter.absoluteYears = [];
    }
  }

  public sortSelection(selection: TreeNode[]): void {
    this.filterUtilService.sortSelection(selection, true);
  }

  private flattenTree(nodes: TreeNode[]): TreeNode[] {
    return this.filterUtilService.flattenTree(nodes);
  }

  private findNodeRecursive(id: FilterTypes, nodes: TreeNode[]) {
    return this.filterUtilService.findNodeRecursive(id, nodes);
  }

  CheckEndGreaterThanBeginning(beginning: string, ending: string): boolean {
    return this.filterUtilService.checkEndGreaterThanBeginning(beginning, ending);
  }

  private setPassIfNaOption(): void {
    if (this.searchStrategy.studySearchStrategyDatabases.length === 1) {
      return;
    }

    this.disablePassIfNa = true;
    this.filter.isPassIfNa = true;
  }
}
